import React, { useEffect, useState } from "react";
import { Container, Spinner, Modal } from "react-bootstrap";
import "../CSS/Payment.css";
import { loadStripe } from "@stripe/stripe-js";
import { BillingService } from "../Services/BillingService";
import { useUserStatus } from "../contexts/UserStatusContext";
import { DebugProvider } from "../contexts/DebugContext";
import DebugToggle from "../components/Debug/DebugToggle";
import RemainingLinksDebugPanel from "../components/Debug/RemainingLinksDebugPanel";
import UpgradeCelebrationModal from "../components/UI/UpgradeCelebrationModal";

import PricingPlan from "../components/UI/PricingPlan";
import CheckoutForm from "../components/UI/CheckoutForm";

import { Elements } from "@stripe/react-stripe-js";

// Read Stripe publishable key from environment (CRA requires REACT_APP_ prefix)
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (!STRIPE_PUBLISHABLE_KEY) {
  // Surface a clear log to help configure environments correctly
  // Note: value is intentionally not printed to avoid leaking secrets
  // Configure .env.development.local and .env.production with REACT_APP_STRIPE_PUBLISHABLE_KEY
  console.error("Stripe publishable key missing: set REACT_APP_STRIPE_PUBLISHABLE_KEY in your env file.");
}

// Only initialize Stripe if we have a valid key
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

const PaymentScreenContent = () => {
  const { 
    userStatus, 
    showUpgradeCelebration, 
    closeUpgradeCelebration, 
    getPlanDisplayName,
    isPro,
    isStarter,
    refreshUserStatus,
    forceSyncSubscriptionStatus
  } = useUserStatus();
  
  const [loading, setLoading] = useState(true);
  
  // Debug: Track UserStatusContext values in PaymentScreen
  console.log('🎇 PaymentScreen render:', {
    userStatus,
    planDisplayName: getPlanDisplayName(),
    isPro: isPro(),
    isStarter: isStarter(),
    showUpgradeCelebration
  });
  const [planListings, setPlanListings] = useState([]);
  const [clientSecret, setClientSecret] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Track userStatus changes in PaymentScreen
  useEffect(() => {
    console.log('🗺 PaymentScreen: userStatus changed to:', {
      userStatus,
      planDisplayName: getPlanDisplayName(),
      timestamp: new Date().toISOString()
    });
  }, [userStatus, getPlanDisplayName]);
  
  useEffect(() => {
  if (clientSecret) {
    console.log("Updated clientSecret:", clientSecret);
    setShowModal(true);
  }
}, [clientSecret]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await BillingService.fetchPaymentPlans({});
        setPlanListings(response.data);
        console.log("*******RESPONSE IS", response.data);
      } catch (error) {
        console.error("Error fetching plans:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Sync subscription status when page becomes visible (catches users returning after cancellation)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        console.log('👁️ Payment page became visible, syncing subscription status...');
        try {
          await forceSyncSubscriptionStatus();
        } catch (error) {
          console.error('❌ Failed to sync subscription status on page visibility:', error);
        }
      }
    };

    // Add event listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also sync on initial mount if user is pro (in case they cancelled while away)
    if (userStatus === 'pro') {
      console.log('🔄 User is pro, performing initial sync check...');
      forceSyncSubscriptionStatus().catch(error => {
        console.error('❌ Failed to perform initial sync check:', error);
      });
    }

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userStatus, forceSyncSubscriptionStatus]);

  const handleBuy = async (selectedPlan) => {
    const planObj = selectedPlan;
    setSelectedPlan(planObj);
    console.log('🎯 Selected plan:', planObj);

    try {
      if (planObj?.interval) {
        // Recurring subscription flow - use Stripe Checkout
        console.log('🛒 Redirecting to Stripe Checkout...', { planId: planObj.id });
        
        const response = await BillingService.createCheckoutSession({
          planId: planObj.id,
        });

        const checkoutUrl = response?.data?.url || response?.url;
        console.log('✅ Checkout URL received:', checkoutUrl);
        
        if (!checkoutUrl) throw new Error('No checkout URL returned from server');
        
        // Redirect to Stripe Checkout
        window.location.href = checkoutUrl;
      } else {
        // One-time payment flow - keep existing Elements flow
        console.log('💵 Creating payment intent...', { planId: planObj.id });
        const response = await BillingService.createPaymentIntent({
          planId: planObj.id,
        });

        const cs = response?.data?.clientSecret;
        console.log('✅ Received client secret?', !!cs, { prefix: cs ? cs.slice(0, 5) : 'none' });
        if (!cs) throw new Error('No client secret returned from server');
        setClientSecret(cs);
      }
    } catch (error) {
      console.error('❌ Error creating payment/subscription:', error);
      alert(error?.response?.data?.message || error.message || 'Initialization failed');
    }
  };

  console.log("PricingPlan:", PricingPlan);
console.log("CheckoutForm:", CheckoutForm);
console.log("CheckoutForm type:", typeof CheckoutForm);
console.log("PricingPlan type:", typeof PricingPlan);



  return (
    <Container fluid className="payment-container">
      {/* Plans always visible */}
      {!loading && (
        <PricingPlan 
          data={planListings} 
          onBuy={handleBuy} 
          userStatus={userStatus}
          planDisplayName={getPlanDisplayName()}
        />
      )}

      {loading && (
        <div className="d-flex justify-content-center">
          <Spinner animation="border" />
        </div>
      )}

      {/* Modal for checkout */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Complete Your Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {clientSecret && stripePromise ? (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <CheckoutForm
                plan={selectedPlan}
                clientSecret={clientSecret}
                onClose={() => setShowModal(false)}
              />
            </Elements>
          ) : !stripePromise ? (
            <div className="text-center p-4">
              <div className="alert alert-warning">
                <strong>Payment system not available</strong><br />
                The Stripe payment system is not configured. Please contact support.
              </div>
            </div>
          ) : (
            <div className="d-flex justify-content-center">
              <Spinner animation="border" />
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Upgrade Celebration Modal */}
      <UpgradeCelebrationModal 
        isOpen={showUpgradeCelebration}
        onClose={closeUpgradeCelebration}
        planName={userStatus === 'pro' ? 'Pro' : 'Starter'}
      />

      {/* Debug Components */}
      <DebugToggle position="bottom-left" />
      <RemainingLinksDebugPanel />

    </Container>
  );
};

// Wrap the component with providers  
// Note: UserStatusProvider removed - PaymentScreen now uses the app-level UserStatusContext
// This fixes the issue where sidebar status didn't update when PaymentScreen was active
const PaymentScreen = () => {
  return (
    <DebugProvider>
      <PaymentScreenContent />
    </DebugProvider>
  );
};

export default PaymentScreen;
